import React, { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CalendarDays, Map, Users, Plus, ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { quotationService } from '@/lib/supabase/quotations';
import { clientService } from '@/lib/supabase/clients';

interface Quotation {
  id: string;
  title: string;
  client: string;
  destination: string[] | string;
  startDate: Date | null;
  endDate: Date | null;
  status: string;
}

interface StatusCount {
  status: string;
  count: number;
  color: string;
}

export default function Dashboard() {
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [clientCount, setClientCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  
  // Calculate status counts
  const getStatusCounts = (): StatusCount[] => {
    const draft = quotations.filter(q => !q.status || q.status?.toLowerCase() === 'draft').length;
    const active = quotations.filter(q => q.status?.toLowerCase() === 'active').length;
    const completed = quotations.filter(q => q.status?.toLowerCase() === 'completed').length;
    
    return [
      { status: 'Rascunho', count: draft, color: 'bg-voah-coral' },
      { status: 'Em Andamento', count: active, color: 'bg-voah-teal' },
      { status: 'Finalizadas', count: completed, color: 'bg-gray-400' }
    ];
  };

  // Load data from Supabase
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // Load quotations
        const quotationsData = await quotationService.getQuotations();
        const formattedQuotations = quotationsData.map(q => ({
          id: q.id,
          title: q.title || 'Sem título',
          client: q.client || '',
          destination: q.destination || [],
          startDate: q.start_date ? new Date(q.start_date) : null,
          endDate: q.end_date ? new Date(q.end_date) : null,
          status: 'draft'
        }));
        setQuotations(formattedQuotations);

        // Load clients
        const clients = await clientService.getClients();
        setClientCount(clients.length);

      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Get recent quotations (up to 3)
  const recentQuotations = quotations.slice(0, 3);

  // Format dates for display
  const formatDateRange = (startDate: Date | null, endDate: Date | null) => {
    if (!startDate && !endDate) return 'Datas não definidas';
    
    if (startDate && endDate) {
      const start = startDate;
      const end = endDate;
      
      const startDay = start.getDate();
      const endDay = end.getDate();
      const startMonth = start.toLocaleString('pt-BR', { month: 'short' });
      const endMonth = end.toLocaleString('pt-BR', { month: 'short' });
      const startYear = start.getFullYear();
      const endYear = end.getFullYear();
      
      if (startYear === endYear && startMonth === endMonth) {
        return `${startDay}-${endDay} ${startMonth}`;
      } else if (startYear === endYear) {
        return `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
      } else {
        return `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`;
      }
    }
    
    if (startDate) {
      const date = startDate;
      return `${date.getDate()} ${date.toLocaleString('pt-BR', { month: 'short' })} ${date.getFullYear()}`;
    }
    
    if (endDate) {
      const date = endDate;
      return `${date.getDate()} ${date.toLocaleString('pt-BR', { month: 'short' })} ${date.getFullYear()}`;
    }
    
    return 'Datas não definidas';
  };

  // Format destination for display
  const formatDestination = (destination: string[] | string): string => {
    if (Array.isArray(destination)) {
      return destination.join(', ');
    }
    return destination || 'Destino não informado';
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
              <CardTitle className="text-sm font-medium">Total de Cotações</CardTitle>
              <Map className="h-4 w-4 text-voah-teal" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{quotations.length}</div>
              <p className="text-xs text-muted-foreground">
                {quotations.length > 0 
                  ? `${quotations.length} ${quotations.length === 1 ? 'cotação cadastrada' : 'cotações cadastradas'}`
                  : 'Comece criando sua primeira cotação'}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
              <CardTitle className="text-sm font-medium">Status das Cotações</CardTitle>
              <CalendarDays className="h-4 w-4 text-voah-teal" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {getStatusCounts().map((status, index) => (
                  <div key={status.status} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{status.status}</span>
                      <span className="font-medium">{status.count}</span>
                    </div>
                    <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${status.color} transition-all duration-500`}
                        style={{ 
                          width: `${quotations.length ? (status.count / quotations.length) * 100 : 0}%`
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
              <CardTitle className="text-sm font-medium">Clientes</CardTitle>
              <Users className="h-4 w-4 text-voah-teal" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{clientCount}</div>
              <p className="text-xs text-muted-foreground">
                {clientCount > 0
                  ? `${clientCount} ${clientCount === 1 ? 'cliente cadastrado' : 'clientes cadastrados'}`
                  : 'Adicione seu primeiro cliente'}
              </p>
            </CardContent>
          </Card>
        </div>

        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Cotações Recentes</CardTitle>
              <Link to="/admin/travel-hub">
                <Button variant="link" className="text-voah-teal">
                  Ver todas
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {recentQuotations.length > 0 ? (
              <div className="space-y-4">
                {recentQuotations.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="space-y-1">
                      <div className="font-medium">{item.client || 'Cliente não informado'}</div>
                      <div className="text-sm text-muted-foreground">
                        {formatDestination(item.destination)} • {formatDateRange(item.startDate, item.endDate)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant="secondary"
                        className="bg-voah-coral"
                      >
                        Rascunho
                      </Badge>
                      <Link to={`/admin/quotations/${item.id}`}>
                        <Button variant="ghost" size="icon">
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">Não há cotações cadastradas</p>
                <Link to="/admin/quotations/new">
                  <Button className="bg-voah-teal hover:bg-voah-teal-dark">
                    <Plus className="mr-2 h-4 w-4" />
                    Criar Cotação
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
} 